import { Form, Input, InputNumber, Row, Col, Typography, Spin } from 'antd';
import { memo, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CustomerApis } from 'apis/CustomerApis';
import TableComponent from 'components/Table/TableComponent';
import { InputForm, SelectForm, LazySelectForm } from 'components';
import InputNumberForm from 'components/Input/InputNumberForn';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { generateUUID } from 'utils/constants/functions';
import {
  formatCurrency,
  formatNumber_v2 as formatNumber,
} from 'utils/constants/functions';
import {
  ILazySelectParams,
  ILazySelectData,
} from 'components/Select/LazySelectForm';

interface IProps {
  form: any;
  customerId?: string;
  sumPaymentData: any;
  setSumPaymentData: (data: any) => any;
  refetchSumPayment: (
    dataNewSaleOrder?: any,
    dataNewPurchaseOrder?: any,
    isCalcAgain?: boolean,
  ) => any;
  loading: boolean;
  lockYn?: boolean;
  detailData?: any; // Data từ getDetailTHTT API để có type và data tính toán
  fetchDebtData?: () => void; // Hàm để reload công nợ
  setSaleOrderSelected?: (value: any) => void; // Hàm để clear bảng kê
  setPurchaseOrderSelected?: (value: any) => void; // Hàm để clear bảng dẻ
}

const TabSumInvoice = (props: IProps) => {
  const {
    form,
    customerId,
    sumPaymentData,
    setSumPaymentData,
    refetchSumPayment,
    loading,
    lockYn,
    detailData,
    fetchDebtData,
    setSaleOrderSelected,
    setPurchaseOrderSelected,
  } = props;
  const [optionsCustomerDebitId, setOptionsCustomerDebitId] =
    useState<ILazySelectData>({
      options: [],
      totalItems: 0,
    });

  const columns: ColumnsTypeCustom = [
    {
      title: 'Q10 (chỉ)',
      width: 80,
      align: 'right',
      render: (record) => {
        // Sử dụng totalQ10Chi đã tính sẵn từ DetailPaymentSummaryPage
        return formatCurrency({
          value: Number(Number(record?.totalQ10Chi || 0).toFixed(4)),
          format: 4,
        });
      },
    },
    {
      title: 'Hao lai (chỉ)',
      width: 100,
      dataIndex: 'totalHaolai',
      align: 'right',
      render: (value) =>
        formatCurrency({
          value: Number(Number(value).toFixed(4)),
          format: 4,
        }),
    },
    {
      title: 'Q10 tổng',
      width: 80,
      align: 'right',
      render: (record) =>
        formatCurrency({
          value: Number(Number(record?.totalQ10Final || 0).toFixed(4)),
          format: 4,
        }),
    },
    {
      title: 'Tiền Món',
      width: 138,
      dataIndex: 'totalUnitPrice',
      align: 'right',
      render: (value) => formatNumber(value),
    },
    {
      title: 'Tiền Công',
      width: 138,
      dataIndex: 'totalprocessPrice',
      align: 'right',
      render: (value) => formatNumber(value),
    },
    {
      title: 'Tiền vàng',
      width: 138,
      dataIndex: 'totalGoldPrice',
      align: 'right',
      render: (value) => formatNumber(value),
    },
    {
      title: 'Tổng thanh toán (tiền mặt)',
      width: 200,
      dataIndex: 'finalPrice',
      align: 'right',
      render: (value) => formatNumber(value),
    },
  ];

  const mutateGetListCustomer = useMutation({
    mutationFn: CustomerApis.getCustomerList,
    onSuccess: (data) => {
      const mappedData = data?.data?.getCustomerList?.items?.map(
        (item: any) => ({
          label: `${item?.sapCustomerId} - ${item?.storeName}`,
          value: item?.id, // value là mã DMS
          customerId: item?.id,
          sapCustomerId: item?.sapCustomerId,
          storeName: item?.storeName,
        }),
      );
      setOptionsCustomerDebitId({
        options: mappedData || [],
        totalItems: mappedData?.length || 0,
      });
    },
    onError: (error) => {
      setOptionsCustomerDebitId({
        options: [],
        totalItems: 0,
      });
    },
  });

  const handleOnChangeNumber = (e: any, form: any) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const fieldName = e.target.name;
    if (
      fieldName === 'pnjpDebitAmountAfter' ||
      fieldName === 'customerDebitAmountAfter'
    )
      return;
    form.setFieldValue(fieldName, value);
  };

  const handleOnChangeNumberWithDecimal = (e: any, form: any) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    const fieldName = e.target.name;
    if (
      fieldName === 'pnjpDebitQ10After' ||
      fieldName === 'customerDebitQ10After'
    )
      return;
    form.setFieldValue(fieldName, value);
  };

  const handleCustomerSelectScrollChange = (params: ILazySelectParams) => {
    // API getCustomerList không cần scroll change, chỉ cần gọi 1 lần
    // Không cần implement gì ở đây
  };

  useEffect(() => {
    if (customerId) {
      mutateGetListCustomer.mutate(customerId);
    } else {
      setOptionsCustomerDebitId({
        options: [],
        totalItems: 0,
      });
    }
  }, [customerId]);

  // Initialize calculationDisplay on mount
  useEffect(() => {
    const initialCalculationDisplay = getCalculationDisplay();
    setCalculationDisplay(initialCalculationDisplay);
  }, [detailData]);

  // Logic tính toán và hiển thị cột
  const getCalculationDisplay = () => {
    if (!detailData) return { showQ10: true, showVND: true };

    const saleOrderType = form.getFieldValue('orderType'); // Từ Bảng kê
    const purchaseOrderType = form.getFieldValue('purchaseType'); // Từ Bảng dẻ
    const isTypeDebit = detailData.type === 'DEB10_01';

    return { showQ10: true, showVND: true };

    // Default case
    console.log('✅ Default - Hiển thị tất cả');
    return { showQ10: true, showVND: true };
  };

  // Tính toán Tổng công nợ: Khách hàng nợ - (-PNJP nợ)
  const getDebtCalculation = () => {
    if (!detailData) return { q10: 0, vnd: 0 };

    const customerQ10 = detailData.customerDebitQ10 || 0;
    const customerAmount = detailData.customerDebitAmount || 0;
    const pnjpQ10 = detailData.pnjpDebitQ10 || 0;
    const pnjpAmount = detailData.pnjpDebitAmount || 0;

    return {
      q10: customerQ10 - -pnjpQ10, // PNJP nợ nên là số âm
      vnd: customerAmount - -pnjpAmount,
    };
  };

  const debtCalculation = getDebtCalculation();

  // Trigger re-render when form values change
  const [orderTypeValue, setOrderTypeValue] = useState();
  const [purchaseTypeValue, setPurchaseTypeValue] = useState();
  const [calculationDisplay, setCalculationDisplay] = useState({
    showQ10: true,
    showVND: true,
  });

  useEffect(() => {
    const currentOrderType = form.getFieldValue('orderType');
    const currentPurchaseType = form.getFieldValue('purchaseType');

    const newCalculationDisplay = getCalculationDisplay();

    setOrderTypeValue(currentOrderType);
    setPurchaseTypeValue(currentPurchaseType);
    setCalculationDisplay(newCalculationDisplay);
  }, [
    form.getFieldValue('orderType'),
    form.getFieldValue('purchaseType'),
    detailData?.type,
  ]);

  return (
    <>
      <TableComponent
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={sumPaymentData ? [sumPaymentData] : []}
        rowKey={(record: any) => record?.id || generateUUID()}
        loading={loading}
        pagination={false}
      />

      {/* PNJP nợ Section */}
      <Row className="mt-3 mb-3 pb-3 border-b">
        <Col span={6}>
          <Typography.Text className="text-base font-bold">
            PNJP nợ
          </Typography.Text>
        </Col>
        <Col span={18}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            {calculationDisplay.showQ10 && (
              <Col span={12} className="h-[80px]">
                <Form.Item label="Q10 tổng" name={'pnjpDebitQ10After'}>
                  <InputNumberForm
                    placeholder="Q10 tự hiển thị"
                    addonAfter="Chỉ"
                    readOnly
                    precision={4}
                    step={0.0001}
                    formatter={(value: any) => {
                      if (!value) return '';
                      // Handle comma as decimal separator
                      const strValue = value.toString();
                      const num = parseFloat(strValue.replace(',', '.'));
                      return num.toFixed(4).replace('.', ','); // ✅ Convert dot to comma
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {calculationDisplay.showVND && (
              <Col span={12} className="h-[80px]">
                <Form.Item label="Tiền mặt" name={'pnjpDebitAmountAfter'}>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Tiền mặt tự hiển thị"
                    addonAfter="VND"
                    readOnly
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                    }
                    parser={(value) => value!.replace(/\./g, '')}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      {/* Khách hàng nợ Section */}
      <Row className="mt-3 mb-3 pb-3 border-b">
        <Col span={6}>
          <Typography.Text className="text-base font-bold">
            Khách hàng nợ
          </Typography.Text>
        </Col>
        <Col span={18}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            {calculationDisplay.showQ10 && (
              <Col span={12} className="h-[80px]">
                <Form.Item label="Q10 tổng" name={'customerDebitQ10After'}>
                  <InputNumberForm
                    placeholder="Q10 tự hiển thị"
                    addonAfter="Chỉ"
                    readOnly
                    precision={4}
                    step={0.0001}
                    formatter={(value: any) => {
                      if (!value) return '';
                      // Handle comma as decimal separator
                      const strValue = value.toString();
                      const num = parseFloat(strValue.replace(',', '.'));
                      return num.toFixed(4).replace('.', ','); // ✅ Convert dot to comma
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {calculationDisplay.showVND && (
              <Col span={12} className="h-[80px]">
                <Form.Item label="Tiền mặt" name={'customerDebitAmountAfter'}>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Tiền mặt tự hiển thị"
                    addonAfter="VND"
                    readOnly
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                    }
                    parser={(value) => value!.replace(/\./g, '')}
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24} className="h-[80px]">
              <Form.Item
                label="Mã khách hàng gán công nợ"
                name={'customerDebitId'}
              >
                <LazySelectForm
                  key={form.getFieldValue('customerDebitId') || 'empty'}
                  placeholder="Chọn khách hàng"
                  data={optionsCustomerDebitId}
                  allowClear={true}
                  disabled={lockYn}
                  onSelectScrollChange={handleCustomerSelectScrollChange}
                  value={form.getFieldValue('customerDebitId')}
                  onChange={(value) => {
                    console.log('🔍 LazySelectForm onChange:', value);
                    // Lưu trực tiếp value (mã DMS) vào form
                    form.setFieldValue('customerDebitId', value);
                    console.log('🔍 Form value after onChange:', {
                      customerDebitId: form.getFieldValue('customerDebitId'),
                    });
                    // Clear bảng kê và bảng dẻ khi đổi khách hàng gán công nợ
                    if (setSaleOrderSelected) {
                      setSaleOrderSelected([]);
                    }
                    if (setPurchaseOrderSelected) {
                      setPurchaseOrderSelected([]);
                    }
                    form.setFieldsValue({
                      saleOrder: [],
                      purchaseOrder: [],
                    });
                    // Reload công nợ khi đổi khách hàng gán công nợ
                    if (fetchDebtData) {
                      fetchDebtData();
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Phương thức thanh toán Section */}
      <Row className="mt-3 mb-3 pb-3 border-b">
        <Col span={6}>
          <Typography.Text className="text-base font-bold">
            Phương thức thanh toán
          </Typography.Text>
        </Col>
        <Col span={18}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            {calculationDisplay.showVND && (
              <Col span={12} className="h-[80px]">
                <Form.Item label="Tiền mặt" name={'codPrice'}>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Nhập giá trị"
                    addonAfter="VND"
                    disabled={lockYn}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                    }
                    parser={(value) => value!.replace(/\./g, '')}
                    onChange={(value) => {
                      refetchSumPayment();
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {calculationDisplay.showQ10 && (
              <Col span={12} className="h-[80px]">
                <Form.Item label="Q10" name={'q10Payment'}>
                  <InputForm
                    allowClear={false}
                    placeholder="Nhập giá trị"
                    suffix={<p className="text-[#909294]">Chỉ</p>}
                    disabled={lockYn}
                    onChange={(e) => {
                      handleOnChangeNumberWithDecimal(e, form);

                      // Tự động tính toán Voucher = Q10 * giá vàng
                      const q10Value = parseFloat(
                        e.target.value?.replace(/,/g, '') || '0',
                      );
                      const marketPrice = detailData?.marketPrice || 0;
                      const voucherValue = q10Value * marketPrice;

                      if (marketPrice > 0) {
                        form.setFieldValue(
                          'voucher',
                          voucherValue.toLocaleString('vi-VN'),
                        );
                      }
                      refetchSumPayment();
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {calculationDisplay.showVND && (
              <Col span={12} className="h-[80px]">
                <Form.Item label="Chuyển khoản" name={'transferPrice'}>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Nhập giá trị"
                    addonAfter="VND"
                    disabled={lockYn}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                    }
                    parser={(value) => value!.replace(/\./g, '')}
                    onChange={(value) => {
                      refetchSumPayment();
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {calculationDisplay.showVND && (
              <Col span={12} className="h-[80px]">
                <Form.Item label="Voucher" name={'voucher'}>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Nhập giá trị"
                    addonAfter="VND"
                    disabled={lockYn}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                    }
                    parser={(value) => value!.replace(/\./g, '')}
                    onChange={(value) => {
                      refetchSumPayment();
                    }}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      <Form.Item label="Nhập ghi chú" name={'totalNote'}>
        <Input.TextArea
          placeholder="Ghi chú..."
          rows={2}
          allowClear
          disabled={lockYn}
        />
      </Form.Item>
    </>
  );
};

export default memo(TabSumInvoice);
